import { useEffect, useState } from 'react';

import { Listbox } from '@headlessui/react';
import { IconAngleDown, IconCheckDefault } from '@iwoca/icons';
import { Pill } from '@iwoca/orion';

export function MultiSelect<T extends { value: string; label: string }>({
  value,
  options = [],
  defaultValue,
  onChange,
  placeholder,
  ...props
}: {
  value: T[];
  options: T[];
  defaultValue?: T[];
  onChange?: (value: T[]) => void;
  placeholder?: string;
  id?: string;
  name?: string;
  'data-testid'?: string;
}) {
  const [selected, setSelected] = useState<T[]>(defaultValue || []);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setSelected(value);
  }, [value]);

  return (
    <Listbox
      as="div"
      id={props.id}
      name={props.name}
      className="relative w-full"
      value={value}
      onChange={(val) => {
        const filteredValues = val.reduce<{ [key: string]: T }>((acc, curr) => {
          if (acc[curr.value]) {
            delete acc[curr.value];
          } else {
            acc[curr.value] = { ...curr };
          }

          return acc;
        }, {});
        const newValues = Object.entries(filteredValues).map(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ([_id, option]) => option,
        );

        setSelected(newValues);
        onChange?.(newValues);
      }}
      multiple
    >
      <Listbox.Button
        as="div"
        className="relative flex min-h-[43px] w-full cursor-pointer flex-wrap items-center gap-s rounded-s border border-solid border-secondary-80 bg-structure-100 px-m py-s text-s font-reg text-primary-10 focus-within:border-primary-40 hover:border-secondary-40"
        data-testid={props['data-testid']}
      >
        {selected.length === 0 && placeholder ? (
          <span className="text-structure-60">{placeholder}</span>
        ) : (
          selected.map((option) => (
            <Pill key={option.value} variant="london" size="small">
              {option.label}
            </Pill>
          ))
        )}
        <IconAngleDown
          className="absolute right-m top-1/2 -translate-y-1/2 fill-structure-60"
          width={16}
          height={16}
        />
      </Listbox.Button>
      <Listbox.Options className="absolute top-[calc(100%+4px)] z-50 w-full list-none overflow-auto rounded-s border border-solid border-secondary-80 bg-structure-100 p-xs shadow-blue-m outline-none">
        {options.map((option) => (
          <Listbox.Option
            key={option.value}
            value={option}
            className={({ active }) =>
              `flex cursor-pointer items-center justify-between rounded-xs px-m py-s text-s font-reg text-primary-10 ${active ? 'bg-secondary-95' : 'bg-structure-100'}`
            }
          >
            {({ selected: isSelected }) => (
              <>
                <span className={isSelected ? 'font-med' : ''}>
                  {option.label}
                </span>
                {isSelected && (
                  <IconCheckDefault
                    className="fill-primary-40"
                    width={14}
                    height={14}
                  />
                )}
              </>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Listbox>
  );
}
